/* eslint-disable */
import { useState, useEffect, useRef } from "react";

const ACCENT  = "#00E5BE";
const ACCENT2 = "#FF6B35";
const DARK    = "#0A0F1E";
const CARD    = "#111827";
const BORDER  = "#1E2D45";

// ─── Tooltip ─────────────────────────────────────────────────────
function Tooltip({ text, children }) {
  const [show, setShow] = useState(false);
  return (
    <span style={{ position:"relative", display:"inline-block" }}
      onMouseEnter={()=>setShow(true)} onMouseLeave={()=>setShow(false)}>
      {children}
      {show && (
        <span style={{
          position:"absolute", bottom:"120%", left:"50%", transform:"translateX(-50%)",
          background:"#1E2D45", color:"#CBD5E1", fontSize:11, padding:"7px 11px",
          borderRadius:7, zIndex:99, border:`1px solid #96B0C8`,
          boxShadow:"0 6px 24px rgba(0,0,0,0.6)", lineHeight:1.6,
          width:230, whiteSpace:"normal", pointerEvents:"none"
        }}>{text}</span>
      )}
    </span>
  );
}

// ─── Sparkline ────────────────────────────────────────────────────
function Sparkline({ data, width=130, height=34 }) {
  const clean = (data||[]).map(Number).filter(v=>!isNaN(v));
  if (clean.length < 2) return null;
  const min = Math.min(...clean), max = Math.max(...clean);
  const range = max - min || 1;
  const pts = clean.map((v,i) => ({
    x: (i/(clean.length-1))*width,
    y: height - ((v-min)/range)*(height-4) - 2
  }));
  const trend = clean[clean.length-1] >= clean[0];
  const col = trend ? "#00E5BE" : "#FF4D4D";
  const d = pts.map((p,i)=>`${i===0?"M":"L"}${p.x},${p.y}`).join(" ");
  const area = `${d} L${pts[pts.length-1].x},${height} L${pts[0].x},${height} Z`;
  return (
    <svg width={width} height={height}>
      <defs>
        <linearGradient id={`gr${col.replace("#","")}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={col} stopOpacity="0.25"/>
          <stop offset="100%" stopColor={col} stopOpacity="0"/>
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#gr${col.replace("#","")})`}/>
      <path d={d} fill="none" stroke={col} strokeWidth="1.8" strokeLinejoin="round"/>
      <circle cx={pts[pts.length-1].x} cy={pts[pts.length-1].y} r="3" fill={col}/>
    </svg>
  );
}

// ─── Bar Chart ────────────────────────────────────────────────────
function BarChart({ data, labels, color=ACCENT, height=120 }) {
  const clean = (data||[]).map(Number);
  if (!clean.length) return <div style={{color:"#96B0C8",fontSize:12,padding:"20px 0"}}>No data</div>;
  const max = Math.max(...clean.map(Math.abs)) || 1;
  return (
    <div style={{ display:"flex", alignItems:"flex-end", gap:8, height }}>
      {clean.map((v,i) => {
        const pct = Math.abs(v)/max;
        const barH = Math.max((height-36)*pct, 2);
        const neg = v < 0;
        return (
          <div key={i} style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", gap:4 }}>
            <span style={{ fontSize:10, color:"#8899AA", whiteSpace:"nowrap" }}>
              {Math.abs(v)>=1000?`${(v/1000).toFixed(1)}K`:v.toFixed(1)}
            </span>
            <div style={{ width:"100%", height:barH, background:neg?`linear-gradient(180deg,#FF4D4D,#CC2222)`:`linear-gradient(180deg,${color},${color}88)`, borderRadius:"4px 4px 0 0" }}/>
            <span style={{ fontSize:10, color:"#8899AA", whiteSpace:"nowrap" }}>{labels?.[i]}</span>
          </div>
        );
      })}
    </div>
  );
}

// ─── Gauge ────────────────────────────────────────────────────────
function Gauge({ score, label }) {
  const pct = Math.min(Math.max((score||0)/100,0),1);
  const angle = pct*180-90;
  const cx=60, cy=60, r=46;
  const col = pct>0.7?"#00E5BE":pct>0.4?"#FFB300":"#FF4D4D";
  function arc(cx,cy,r,a1,a2){
    function pt(a){const rad=((a-90)*Math.PI)/180;return{x:cx+r*Math.cos(rad),y:cy+r*Math.sin(rad)};}
    const s=pt(a2),e=pt(a1);
    return `M${s.x} ${s.y} A${r} ${r} 0 ${a2-a1<=180?"0":"1"} 0 ${e.x} ${e.y}`;
  }
  const needle=(()=>{const rad=((angle-90)*Math.PI)/180;return{x:cx+32*Math.cos(rad),y:cy+32*Math.sin(rad)};})();
  return (
    <div style={{ textAlign:"center" }}>
      <svg width={120} height={70} viewBox="0 0 120 70">
        <path d={arc(cx,cy,r,-180,0)} fill="none" stroke="#1E2D45" strokeWidth="9" strokeLinecap="round"/>
        <path d={arc(cx,cy,r,-180,angle)} fill="none" stroke={col} strokeWidth="9" strokeLinecap="round"/>
        <line x1={cx} y1={cy} x2={needle.x} y2={needle.y} stroke={col} strokeWidth="2" strokeLinecap="round"/>
        <circle cx={cx} cy={cy} r="4" fill={col}/>
      </svg>
      <div style={{ marginTop:-14, fontSize:20, fontWeight:700, color:col, fontFamily:"'Space Mono',monospace" }}>{Math.round(score||0)}</div>
      <div style={{ fontSize:10, color:"#8899AA", marginTop:2 }}>{label}</div>
    </div>
  );
}

// ─── Metric Card ─────────────────────────────────────────────────
function MetricCard({ title, values, unit="", good="high", tooltip, accent }) {
  const nums = (values||[]).map(Number).filter(v=>!isNaN(v));
  const latest = nums[nums.length-1];
  const prev   = nums[nums.length-2];
  const chg    = prev!=null&&prev!==0?((latest-prev)/Math.abs(prev))*100:null;
  const ok     = good==="high"?chg>=0:chg<=0;
  const fmt    = v => Math.abs(v)>=10000?`${(v/1000).toFixed(1)}K`:Math.abs(v)>=1?v.toFixed(1):v.toFixed(2);
  return (
    <div style={{ background:CARD, border:`1px solid ${BORDER}`, borderRadius:12, padding:"15px 17px", display:"flex", flexDirection:"column", gap:7 }}>
      <Tooltip text={tooltip||title}>
        <span style={{ fontSize:10, color:"#8899AA", textTransform:"uppercase", letterSpacing:"0.07em", cursor:"help", borderBottom:"1px dashed #1E2D45" }}>{title} ℹ</span>
      </Tooltip>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-end" }}>
        <div>
          <span style={{ fontSize:21, fontWeight:700, color:accent||"#E8F0FE", fontFamily:"'Space Mono',monospace" }}>
            {latest!=null?fmt(latest):"—"}
          </span>
          <span style={{ fontSize:11, color:"#8899AA", marginLeft:4 }}>{unit}</span>
        </div>
        {chg!=null&&<span style={{ fontSize:11, color:ok?"#00E5BE":"#FF4D4D", fontWeight:700 }}>{chg>=0?"▲":"▼"}{Math.abs(chg).toFixed(1)}%</span>}
      </div>
      <Sparkline data={nums} width={130} height={32}/>
    </div>
  );
}

// ─── Section header ───────────────────────────────────────────────
function Section({ title, accent=ACCENT, sub, children }) {
  return (
    <div style={{ marginBottom:30 }}>
      <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:sub?6:14 }}>
        <div style={{ width:3, height:18, background:accent, borderRadius:2 }}/>
        <h3 style={{ margin:0, fontSize:12, fontWeight:700, color:"#CBD5E1", textTransform:"uppercase", letterSpacing:"0.1em" }}>{title}</h3>
      </div>
      {sub&&<div style={{ fontSize:11, color:"#8BA3BC", marginBottom:14, marginLeft:13 }}>{sub}</div>}
      {children}
    </div>
  );
}

// ─── DCF Engine (runs in-browser) ────────────────────────────────
function runDCF(assumptions) {
  const { baseFCF, growthRate1, growthRate2, terminalGrowth, wacc, netDebt, sharesOutstanding, years1=5, years2=5 } = assumptions;
  if (!baseFCF||!wacc||!sharesOutstanding) return null;
  const waccD = wacc/100, g1 = growthRate1/100, g2 = growthRate2/100, gt = terminalGrowth/100;

  let pvFCFs = [], fcf = baseFCF, totalPV = 0;
  // Phase 1 - high growth
  for (let i=1; i<=years1; i++) {
    fcf = fcf * (1 + g1);
    const pv = fcf / Math.pow(1+waccD, i);
    pvFCFs.push({ year:`Y${i}`, fcf: Math.round(fcf), pv: Math.round(pv), phase:1 });
    totalPV += pv;
  }
  // Phase 2 - fade
  for (let i=1; i<=years2; i++) {
    fcf = fcf * (1 + g2);
    const pv = fcf / Math.pow(1+waccD, years1+i);
    pvFCFs.push({ year:`Y${years1+i}`, fcf: Math.round(fcf), pv: Math.round(pv), phase:2 });
    totalPV += pv;
  }
  // Terminal value (Gordon Growth)
  const terminalFCF = fcf * (1 + gt);
  const terminalValue = terminalFCF / (waccD - gt);
  const pvTerminal = terminalValue / Math.pow(1+waccD, years1+years2);
  totalPV += pvTerminal;

  const enterpriseValue = totalPV;
  const equityValue = enterpriseValue - (netDebt||0);
  const intrinsicPerShare = equityValue / sharesOutstanding;

  return {
    pvFCFs,
    pvPhase1: pvFCFs.filter(x=>x.phase===1).reduce((a,b)=>a+b.pv,0),
    pvPhase2: pvFCFs.filter(x=>x.phase===2).reduce((a,b)=>a+b.pv,0),
    terminalValue: Math.round(terminalValue),
    pvTerminal: Math.round(pvTerminal),
    enterpriseValue: Math.round(enterpriseValue),
    equityValue: Math.round(equityValue),
    intrinsicPerShare: Math.round(intrinsicPerShare),
    tvWeightPct: Math.round((pvTerminal/totalPV)*100)
  };
}

function runGordon(assumptions) {
  const { dividendPerShare, dividendGrowth, costOfEquity } = assumptions;
  if (!dividendPerShare||!costOfEquity) return null;
  const ke = costOfEquity/100, g = dividendGrowth/100;
  if (ke <= g) return null;
  const d1 = dividendPerShare * (1 + g);
  const intrinsicPerShare = d1 / (ke - g);
  return {
    d1: d1.toFixed(2),
    intrinsicPerShare: Math.round(intrinsicPerShare)
  };
}

// Sensitivity table: rows = WACC, cols = terminal growth
function buildSensitivity(assumptions) {
  const waccRange = [-1,-0.5,0,+0.5,+1].map(d => +(assumptions.wacc+d).toFixed(1));
  const gtRange   = [-0.5,0,+0.5,+1,+1.5].map(d => +(assumptions.terminalGrowth+d).toFixed(1));
  return { waccRange, gtRange,
    cells: waccRange.map(w => gtRange.map(gt => {
      const r = runDCF({...assumptions, wacc:w, terminalGrowth:gt});
      return r ? r.intrinsicPerShare : null;
    }))
  };
}

// ─── Slider input ─────────────────────────────────────────────────
function SliderInput({ label, value, onChange, min, max, step=0.5, unit="", tooltip }) {
  return (
    <div style={{ marginBottom:14 }}>
      <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
        <Tooltip text={tooltip}>
          <span style={{ fontSize:11, color:"#8899AA", cursor:"help", borderBottom:"1px dashed #1E2D45" }}>{label} ℹ</span>
        </Tooltip>
        <span style={{ fontSize:12, color:ACCENT, fontFamily:"'Space Mono',monospace", fontWeight:700 }}>
          {Number(value).toFixed(step<1?1:0)}{unit}
        </span>
      </div>
      <input type="range" min={min} max={max} step={step} value={value} onChange={e=>onChange(Number(e.target.value))}
        style={{ width:"100%", accentColor:ACCENT, cursor:"pointer", height:4 }}/>
      <div style={{ display:"flex", justifyContent:"space-between", marginTop:2 }}>
        <span style={{ fontSize:10, color:"#96B0C8" }}>{min}{unit}</span>
        <span style={{ fontSize:10, color:"#96B0C8" }}>{max}{unit}</span>
      </div>
    </div>
  );
}

// ─── Prompt ───────────────────────────────────────────────────────
function buildPrompt(ticker, exchange) {
  return `You are a senior financial analyst. Produce a comprehensive 3-year financial health report AND intrinsic valuation for the stock ticker: ${ticker} listed on ${exchange}.

Search the web for the most recent 3 fiscal years of data. Return ONLY a single raw JSON object — no markdown, no backticks, no text outside the JSON.

{
  "company": {
    "name": "string",
    "ticker": "${ticker}",
    "exchange": "${exchange}",
    "sector": "string",
    "industry": "string",
    "description": "2-3 sentences",
    "marketCap": "string e.g. ₹1,25,000 Cr",
    "currentPrice": "string",
    "currentPriceNum": number,
    "week52High": "string",
    "week52Low": "string",
    "peRatio": number,
    "pbRatio": number,
    "dividendYield": number,
    "sharesOutstanding": number_in_crores_or_millions,
    "sharesUnit": "Cr shares or M shares"
  },
  "years": ["FY2022","FY2023","FY2024"],
  "currency": "₹ Cr",
  "currencySymbol": "₹",
  "financials": {
    "revenue": [n,n,n],
    "ebitda": [n,n,n],
    "ebit": [n,n,n],
    "netProfit": [n,n,n],
    "eps": [n,n,n],
    "totalAssets": [n,n,n],
    "totalDebt": [n,n,n],
    "cash": [n,n,n],
    "netDebt": [n,n,n],
    "shareholderEquity": [n,n,n],
    "operatingCashFlow": [n,n,n],
    "freeCashFlow": [n,n,n],
    "capex": [n,n,n],
    "dividendsPaid": [n,n,n],
    "dividendPerShare": [n,n,n],
    "investedCapital": [n,n,n],
    "nopat": [n,n,n],
    "wacc": [n,n,n],
    "enterpriseValue": [n,n,n]
  },
  "ratios": {
    "profitability": { "grossMargin":[n,n,n],"ebitdaMargin":[n,n,n],"ebitMargin":[n,n,n],"netMargin":[n,n,n],"roe":[n,n,n],"roa":[n,n,n],"roce":[n,n,n],"roic":[n,n,n] },
    "valuation": { "peRatio":[n,n,n],"pbRatio":[n,n,n],"psRatio":[n,n,n],"pegRatio":[n,n,n],"evToEbitda":[n,n,n],"evToEbit":[n,n,n],"evToSales":[n,n,n],"evToFcf":[n,n,n],"fcfYield":[n,n,n] },
    "leverage": { "debtToEquity":[n,n,n],"debtToEbitda":[n,n,n],"netDebtToEbitda":[n,n,n],"interestCoverage":[n,n,n],"debtToAssets":[n,n,n] },
    "liquidity": { "currentRatio":[n,n,n],"quickRatio":[n,n,n],"cashRatio":[n,n,n],"operatingCashFlowRatio":[n,n,n] },
    "efficiency": { "assetTurnover":[n,n,n],"inventoryTurnover":[n,n,n],"receivablesTurnover":[n,n,n],"dso":[n,n,n],"dio":[n,n,n],"dpo":[n,n,n],"cashConversionCycle":[n,n,n] },
    "growth": { "revenueGrowth":[n,n,n],"ebitdaGrowth":[n,n,n],"netProfitGrowth":[n,n,n],"epsGrowth":[n,n,n],"fcfGrowth":[n,n,n] },
    "valueCreation": { "eva":[n,n,n],"economicSpread":[n,n,n],"fcfConversion":[n,n,n],"altmanZScore":[n,n,n],"piotroskiScore":[n,n,n] }
  },
  "healthScores": { "overall":0-100,"profitability":0-100,"valuation":0-100,"liquidity":0-100,"leverage":0-100,"growth":0-100,"efficiency":0-100,"valueCreation":0-100 },
  "dcfAssumptions": {
    "baseFCF": latest_free_cash_flow_number,
    "growthRate1": suggested_FCF_growth_pct_phase1_years1to5,
    "growthRate2": suggested_FCF_growth_pct_phase2_years6to10,
    "terminalGrowth": suggested_terminal_growth_rate_pct,
    "wacc": suggested_wacc_pct,
    "netDebt": latest_net_debt_number,
    "sharesOutstanding": shares_in_same_unit_as_currency,
    "dividendPerShare": latest_annual_dividend_per_share,
    "dividendGrowth": expected_dividend_growth_rate_pct,
    "costOfEquity": estimated_cost_of_equity_pct,
    "analystNote": "1-2 sentence explanation of why these assumptions were chosen"
  },
  "verdict": {
    "rating": "BUY or HOLD or AVOID",
    "targetPE": number,
    "strengths": ["s1","s2","s3","s4"],
    "concerns": ["c1","c2","c3"],
    "summary": "2-3 sentence analyst conclusion"
  }
}

Use web search to find real data. Use null for any figure you cannot find. Return ONLY the JSON object.`;
}

// ─── App ──────────────────────────────────────────────────────────
export default function App() {
  const [ticker,   setTicker]   = useState("");
  const [exchange, setExchange] = useState("NSE");
  const [loading,  setLoading]  = useState(false);
  const [report,   setReport]   = useState(null);
  const [error,    setError]    = useState("");
  const [progress, setProgress] = useState("");
  const [tab,      setTab]      = useState("overview");

  // DCF assumption sliders (populated from API response, then user-adjustable)
  const [dcfA, setDcfA] = useState({
    baseFCF:1000, growthRate1:12, growthRate2:8, terminalGrowth:4,
    wacc:10, netDebt:0, sharesOutstanding:100,
    dividendPerShare:20, dividendGrowth:6, costOfEquity:12
  });

  const inputRef = useRef(null);
  useEffect(()=>{ inputRef.current?.focus(); },[]);

  const run = async () => {
    if (!ticker.trim()) return;
    setLoading(true); setError(""); setReport(null); setTab("overview");
    setProgress("Searching live financial databases…");
    try {
      const res = await fetch("https://api.anthropic.com/v1/messages", {
        method:"POST",
        headers:{"Content-Type":"application/json","x-api-key":process.env.REACT_APP_ANTHROPIC_KEY,"anthropic-version":"2023-06-01","anthropic-dangerous-direct-browser-access":"true"},
        body: JSON.stringify({
          model:"claude-sonnet-4-20250514",
          max_tokens:7000,
          tools:[{type:"web_search_20250305",name:"web_search"}],
          messages:[{role:"user",content:buildPrompt(ticker.toUpperCase(),exchange)}]
        })
      });
      setProgress("Parsing data and building valuation model…");
      const data = await res.json();
      if (data.error) throw new Error(data.error.message);
      const text = (data.content||[]).filter(b=>b.type==="text").map(b=>b.text).join("");
      const m = text.match(/\{[\s\S]*\}/);
      if (!m) throw new Error("Could not extract data. Try a major listed ticker.");
      const parsed = JSON.parse(m[0]);
      setReport(parsed);
      // Seed DCF sliders from API assumptions
      if (parsed.dcfAssumptions) {
        const a = parsed.dcfAssumptions;
        setDcfA({
          baseFCF: a.baseFCF||1000,
          growthRate1: a.growthRate1||12,
          growthRate2: a.growthRate2||8,
          terminalGrowth: a.terminalGrowth||4,
          wacc: a.wacc||10,
          netDebt: a.netDebt||0,
          sharesOutstanding: a.sharesOutstanding||100,
          dividendPerShare: a.dividendPerShare||10,
          dividendGrowth: a.dividendGrowth||6,
          costOfEquity: a.costOfEquity||12,
        });
      }
      setTab("overview");
    } catch(e) { setError(e.message); }
    finally { setLoading(false); setProgress(""); }
  };

  const R = report;
  const yr  = R?.years || [];
  const fin = R?.financials || {};
  const rat = R?.ratios || {};
  const cur = R?.currency || "";
  const sym = R?.currencySymbol || "₹";

  // Live DCF computation
  const dcfResult  = report ? runDCF(dcfA) : null;
  const gordResult = report ? runGordon(dcfA) : null;

  // Blended intrinsic value (simple average of both methods if both available)
  const blendedIV = dcfResult && gordResult
    ? Math.round((dcfResult.intrinsicPerShare + gordResult.intrinsicPerShare) / 2)
    : dcfResult?.intrinsicPerShare || gordResult?.intrinsicPerShare || null;

  const currentPriceNum = R?.company?.currentPriceNum || null;
  const marginOfSafety  = blendedIV && currentPriceNum ? ((blendedIV - currentPriceNum)/blendedIV*100).toFixed(1) : null;

  const ivRec = blendedIV && currentPriceNum
    ? (currentPriceNum < blendedIV * 0.85 ? "STRONG BUY"
      : currentPriceNum < blendedIV ? "BUY"
      : currentPriceNum < blendedIV * 1.15 ? "HOLD"
      : "AVOID")
    : null;

  const ivRecColor = {
    "STRONG BUY":"#00E5BE","BUY":"#27AE60","HOLD":"#FFB300","AVOID":"#FF4D4D"
  }[ivRec] || "#8899AA";

  const sens = report && dcfResult ? buildSensitivity(dcfA) : null;

  const TABS = [
    {id:"overview",label:"Overview"},
    {id:"profitability",label:"Profitability"},
    {id:"valuation",label:"Valuation"},
    {id:"leverage",label:"Leverage"},
    {id:"liquidity",label:"Liquidity"},
    {id:"efficiency",label:"Efficiency"},
    {id:"value",label:"Value Creation"},
    {id:"cashflow",label:"Cash Flow"},
    {id:"dcf",label:"🎯 Intrinsic Value"},
  ];

  return (
    <div style={{ minHeight:"100vh", background:DARK, fontFamily:"'IBM Plex Sans',sans-serif", color:"#E8F0FE" }}>
      <link href="https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:ital,wght@0,400;0,600;0,700;1,400&family=Space+Mono:wght@400;700&display=swap" rel="stylesheet"/>
      <style>{`*{box-sizing:border-box}::-webkit-scrollbar{width:4px;height:4px}::-webkit-scrollbar-thumb{background:#1E2D45;border-radius:4px}
        @keyframes spin{to{transform:rotate(360deg)}}
        @keyframes fadeUp{from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:none}}
        @keyframes pulse{0%,100%{opacity:1}50%{opacity:0.4}}
        @keyframes glow{0%,100%{box-shadow:0 0 12px #00E5BE33}50%{box-shadow:0 0 28px #00E5BE66}}
      `}</style>

      {/* ─── Header ─── */}
      <div style={{ background:"linear-gradient(135deg,#0D1B2E,#0A0F1E)", borderBottom:`1px solid ${BORDER}`, padding:"26px 36px 22px" }}>
        <div style={{ maxWidth:1100, margin:"0 auto" }}>
          <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:18 }}>
            <div style={{ width:40,height:40,borderRadius:11,background:`linear-gradient(135deg,${ACCENT},#007A65)`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:22 }}>📊</div>
            <div>
              <div style={{ fontSize:21,fontWeight:700,letterSpacing:"-0.02em" }}>FinScope <span style={{ color:ACCENT }}>Pro</span></div>
              <div style={{ fontSize:10,color:"#8BA3BC",letterSpacing:"0.09em" }}>AI-POWERED FINANCIAL HEALTH · DCF + GORDON GROWTH INTRINSIC VALUATION</div>
            </div>
          </div>
          <div style={{ display:"flex",gap:10,flexWrap:"wrap" }}>
            <select value={exchange} onChange={e=>setExchange(e.target.value)} style={{ background:"#111827",border:`1px solid ${BORDER}`,color:"#CBD5E1",padding:"11px 14px",borderRadius:8,fontSize:13,fontFamily:"inherit",outline:"none" }}>
              {["NSE","BSE","NYSE","NASDAQ","LSE","TSX","ASX","SGX","HKEx","Euronext"].map(x=><option key={x}>{x}</option>)}
            </select>
            <input ref={inputRef} value={ticker} onChange={e=>setTicker(e.target.value.toUpperCase())} onKeyDown={e=>e.key==="Enter"&&run()}
              placeholder="e.g. RELIANCE, TCS, AAPL, TSLA, NVDA…"
              style={{ flex:1,minWidth:220,background:"#111827",border:`1px solid ${BORDER}`,color:"#E8F0FE",padding:"11px 16px",borderRadius:8,fontSize:14,fontFamily:"'Space Mono',monospace",outline:"none",transition:"border-color 0.2s" }}
              onFocus={e=>e.target.style.borderColor=ACCENT} onBlur={e=>e.target.style.borderColor=BORDER}/>
            <button onClick={run} disabled={loading||!ticker.trim()} style={{ background:loading?"#1E2D45":`linear-gradient(135deg,${ACCENT},#00B899)`,border:"none",color:loading?"#8BA3BC":"#0A0F1E",padding:"11px 28px",borderRadius:8,fontSize:13,fontWeight:700,cursor:loading?"not-allowed":"pointer",fontFamily:"inherit",letterSpacing:"0.05em" }}>
              {loading?"Analysing…":"Generate Report →"}
            </button>
          </div>
          <div style={{ display:"flex",gap:8,marginTop:12,flexWrap:"wrap" }}>
            {[["RELIANCE","NSE"],["TCS","NSE"],["HDFCBANK","NSE"],["AAPL","NASDAQ"],["TSLA","NYSE"],["NVDA","NASDAQ"]].map(([t,e])=>(
              <span key={t} onClick={()=>{setTicker(t);setExchange(e);}} style={{ background:"#111827",border:`1px solid ${BORDER}`,color:"#94A3B8",padding:"4px 12px",borderRadius:20,fontSize:11,cursor:"pointer",fontFamily:"'Space Mono',monospace" }}>{t} · {e}</span>
            ))}
          </div>
        </div>
      </div>

      {/* ─── Body ─── */}
      <div style={{ maxWidth:1100, margin:"0 auto", padding:"32px 36px 60px" }}>

        {loading&&(
          <div style={{ textAlign:"center",padding:"80px 0" }}>
            <div style={{ width:60,height:60,border:`3px solid ${BORDER}`,borderTop:`3px solid ${ACCENT}`,borderRadius:"50%",animation:"spin 0.9s linear infinite",margin:"0 auto 20px" }}/>
            <div style={{ color:ACCENT,fontSize:13,fontFamily:"'Space Mono',monospace",animation:"pulse 1.5s infinite" }}>{progress}</div>
            <div style={{ color:"#96B0C8",fontSize:12,marginTop:8 }}>Fetching financials · building DCF · computing intrinsic value…</div>
          </div>
        )}

        {error&&<div style={{ background:"#1A0A0A",border:"1px solid #4D1515",borderRadius:12,padding:"16px 20px",color:"#FF6B6B",fontSize:13 }}>⚠ {error}</div>}

        {!loading&&!report&&!error&&(
          <div style={{ textAlign:"center",padding:"80px 0" }}>
            <div style={{ fontSize:72,marginBottom:14,opacity:0.1 }}>📈</div>
            <div style={{ fontSize:15,color:"#96B0C8" }}>Enter a stock ticker to generate a full financial health + intrinsic valuation report</div>
            <div style={{ fontSize:12,color:"#96B0C8",marginTop:6 }}>30+ ratios · PEG · EV/EBITDA · EVA · DCF Valuation · Gordon Growth Model · Sensitivity Matrix</div>
          </div>
        )}

        {report&&!loading&&(
          <div style={{ animation:"fadeUp 0.5s ease" }}>

            {/* Company Banner */}
            <div style={{ background:"linear-gradient(135deg,#0D1B2E,#111827)",border:`1px solid ${BORDER}`,borderRadius:16,padding:"24px 28px",marginBottom:22,display:"flex",justifyContent:"space-between",alignItems:"flex-start",flexWrap:"wrap",gap:16 }}>
              <div style={{ flex:1,minWidth:260 }}>
                <div style={{ fontSize:23,fontWeight:700,marginBottom:6 }}>{R.company?.name}</div>
                <div style={{ display:"flex",gap:8,flexWrap:"wrap",marginBottom:12 }}>
                  <span style={{ background:"#1E2D45",color:ACCENT,padding:"3px 10px",borderRadius:20,fontSize:11,fontFamily:"'Space Mono',monospace" }}>{R.company?.exchange} : {R.company?.ticker}</span>
                  <span style={{ background:"#1E2D45",color:"#8899AA",padding:"3px 10px",borderRadius:20,fontSize:11 }}>{R.company?.sector}</span>
                  <span style={{ background:"#1E2D45",color:"#8899AA",padding:"3px 10px",borderRadius:20,fontSize:11 }}>{R.company?.industry}</span>
                </div>
                <div style={{ fontSize:12,color:"#8BA3BC",lineHeight:1.8,maxWidth:520 }}>{R.company?.description}</div>
                <div style={{ display:"flex",gap:20,marginTop:14,flexWrap:"wrap" }}>
                  {[["52W High",R.company?.week52High],["52W Low",R.company?.week52Low],["P/E",R.company?.peRatio],["P/B",R.company?.pbRatio],["Div Yield",R.company?.dividendYield+"%"]].map(([k,v])=>(
                    <div key={k}><div style={{ fontSize:10,color:"#8BA3BC" }}>{k}</div><div style={{ fontSize:13,color:"#CBD5E1",fontFamily:"'Space Mono',monospace" }}>{v??"—"}</div></div>
                  ))}
                </div>
              </div>
              <div style={{ textAlign:"right" }}>
                <div style={{ fontSize:30,fontWeight:700,color:ACCENT,fontFamily:"'Space Mono',monospace" }}>{R.company?.currentPrice}</div>
                <div style={{ fontSize:12,color:"#8899AA",marginBottom:8 }}>Mkt Cap: {R.company?.marketCap}</div>
                {/* IV Summary pill */}
                {blendedIV&&(
                  <div style={{ marginBottom:8 }}>
                    <div style={{ fontSize:10,color:"#8BA3BC",marginBottom:3 }}>Intrinsic Value (Blended)</div>
                    <div style={{ fontSize:20,fontWeight:700,color:"#FFB300",fontFamily:"'Space Mono',monospace" }}>{sym}{blendedIV.toLocaleString()}</div>
                  </div>
                )}
                {ivRec&&(
                  <div style={{ display:"inline-block",background:ivRecColor+"22",border:`1px solid ${ivRecColor}55`,color:ivRecColor,padding:"6px 20px",borderRadius:24,fontSize:13,fontWeight:700,letterSpacing:"0.1em",animation:"glow 3s infinite" }}>
                    {ivRec}
                  </div>
                )}
              </div>
            </div>

            {/* Health Gauges */}
            <Section title="Financial Health Scores" accent={ACCENT} sub="AI composite scores across 8 analytical dimensions (0–100)">
              <div style={{ background:CARD,border:`1px solid ${BORDER}`,borderRadius:16,padding:"22px 24px" }}>
                <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(110px,1fr))",gap:10 }}>
                  {[["overall","Overall"],["profitability","Profitability"],["valuation","Valuation"],["liquidity","Liquidity"],["leverage","Leverage"],["growth","Growth"],["efficiency","Efficiency"],["valueCreation","Value Creation"]].map(([k,l])=>(
                    <Gauge key={k} score={R.healthScores?.[k]??50} label={l}/>
                  ))}
                </div>
              </div>
            </Section>

            {/* Tab Nav */}
            <div style={{ display:"flex",gap:2,flexWrap:"wrap",borderBottom:`1px solid ${BORDER}`,marginBottom:28 }}>
              {TABS.map(t=>(
                <button key={t.id} onClick={()=>setTab(t.id)} style={{ background:"none",border:"none",cursor:"pointer",padding:"9px 14px",fontSize:12,fontWeight:600,fontFamily:"inherit",color:tab===t.id?(t.id==="dcf"?"#FFB300":ACCENT):(t.id==="dcf"?"#8BA3BC":"#8BA3BC"),borderBottom:tab===t.id?`2px solid ${t.id==="dcf"?"#FFB300":ACCENT}`:"2px solid transparent",marginBottom:-1,transition:"color 0.2s" }}>
                  {t.label}
                </button>
              ))}
            </div>

            {/* ════ OVERVIEW ════ */}
            {tab==="overview"&&(
              <div style={{ animation:"fadeUp 0.3s ease" }}>
                <Section title="Revenue, EBITDA & Net Profit" accent="#FFB300">
                  <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(185px,1fr))",gap:12,marginBottom:16 }}>
                    <MetricCard title="Revenue" values={fin.revenue} unit={cur} good="high" tooltip="Total top-line sales / net revenue"/>
                    <MetricCard title="EBITDA" values={fin.ebitda} unit={cur} good="high" tooltip="Earnings before interest, tax, depreciation & amortisation"/>
                    <MetricCard title="Net Profit" values={fin.netProfit} unit={cur} good="high" tooltip="Bottom-line profit attributable to shareholders"/>
                    <MetricCard title="EPS" values={fin.eps} unit={sym} good="high" tooltip="Earnings Per Share = Net Profit ÷ Shares Outstanding"/>
                  </div>
                  <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:14 }}>
                    {[["Revenue",fin.revenue,ACCENT],["Net Profit",fin.netProfit,"#FFB300"]].map(([t,d,c])=>(
                      <div key={t} style={{ background:CARD,border:`1px solid ${BORDER}`,borderRadius:12,padding:"16px 18px" }}>
                        <div style={{ fontSize:10,color:"#8899AA",textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8 }}>{t} — {cur}</div>
                        <BarChart data={d} labels={yr} color={c} height={130}/>
                      </div>
                    ))}
                  </div>
                </Section>
                <Section title="Analyst Verdict" accent={ACCENT}>
                  <div style={{ background:"linear-gradient(135deg,#0D1B2E,#111827)",border:`1px solid ${BORDER}`,borderRadius:16,padding:"24px 28px" }}>
                    <p style={{ fontSize:14,color:"#CBD5E1",lineHeight:1.8,marginBottom:20,fontStyle:"italic" }}>"{R.verdict?.summary}"</p>
                    <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:20 }}>
                      <div>
                        <div style={{ fontSize:10,color:ACCENT,textTransform:"uppercase",letterSpacing:"0.1em",marginBottom:10,fontWeight:700 }}>✦ Strengths</div>
                        {R.verdict?.strengths?.map((s,i)=><div key={i} style={{ fontSize:13,color:"#8DB4A0",marginBottom:8,display:"flex",gap:8 }}><span style={{ color:ACCENT }}>▸</span>{s}</div>)}
                      </div>
                      <div>
                        <div style={{ fontSize:10,color:"#FF6B6B",textTransform:"uppercase",letterSpacing:"0.1em",marginBottom:10,fontWeight:700 }}>⚠ Watch Points</div>
                        {R.verdict?.concerns?.map((c,i)=><div key={i} style={{ fontSize:13,color:"#B4898D",marginBottom:8,display:"flex",gap:8 }}><span style={{ color:"#FF6B6B" }}>▸</span>{c}</div>)}
                      </div>
                    </div>
                    <div style={{ marginTop:16,padding:"11px 15px",background:"#0A0F1E",borderRadius:8,fontSize:11,color:"#8BA3BC" }}>⚠ Educational purposes only. Not investment advice. Verify all data from official sources.</div>
                  </div>
                </Section>
              </div>
            )}

            {/* ════ PROFITABILITY ════ */}
            {tab==="profitability"&&(
              <div style={{ animation:"fadeUp 0.3s ease" }}>
                <Section title="Margin Analysis" accent="#FFB300" sub="Revenue conversion to profit at each P&L level">
                  <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(185px,1fr))",gap:12 }}>
                    <MetricCard title="Gross Margin" values={rat.profitability?.grossMargin} unit="%" good="high" tooltip="(Revenue – COGS) / Revenue. Reflects pricing power"/>
                    <MetricCard title="EBITDA Margin" values={rat.profitability?.ebitdaMargin} unit="%" good="high" tooltip="EBITDA / Revenue. Operating efficiency before D&A"/>
                    <MetricCard title="EBIT Margin" values={rat.profitability?.ebitMargin} unit="%" good="high" tooltip="Operating margin after depreciation, before tax and interest"/>
                    <MetricCard title="Net Margin" values={rat.profitability?.netMargin} unit="%" good="high" tooltip="Net Profit / Revenue. True bottom-line efficiency"/>
                  </div>
                </Section>
                <Section title="Return Ratios" accent="#00C9FF" sub="How efficiently capital is deployed">
                  <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(185px,1fr))",gap:12 }}>
                    <MetricCard title="ROE" values={rat.profitability?.roe} unit="%" good="high" accent="#00C9FF" tooltip="Net Profit / Equity. >15% is good"/>
                    <MetricCard title="ROA" values={rat.profitability?.roa} unit="%" good="high" accent="#00C9FF" tooltip="Net Profit / Assets. >5% is decent"/>
                    <MetricCard title="ROCE" values={rat.profitability?.roce} unit="%" good="high" accent="#00C9FF" tooltip="EBIT / Capital Employed. Must exceed WACC"/>
                    <MetricCard title="ROIC" values={rat.profitability?.roic} unit="%" good="high" accent={ACCENT} tooltip="NOPAT / Invested Capital. ROIC > WACC = value creation"/>
                  </div>
                </Section>
              </div>
            )}

            {/* ════ VALUATION ════ */}
            {tab==="valuation"&&(
              <div style={{ animation:"fadeUp 0.3s ease" }}>
                <Section title="Price Multiples" accent="#9B59B6">
                  <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(185px,1fr))",gap:12 }}>
                    <MetricCard title="P/E Ratio" values={rat.valuation?.peRatio} unit="x" good="low" accent="#9B59B6" tooltip="Price / EPS. Compare vs peers and own history"/>
                    <MetricCard title="P/B Ratio" values={rat.valuation?.pbRatio} unit="x" good="low" accent="#9B59B6" tooltip="Price / Book. <1 may indicate undervaluation"/>
                    <MetricCard title="P/S Ratio" values={rat.valuation?.psRatio} unit="x" good="low" accent="#9B59B6" tooltip="Price / Sales per share"/>
                    <MetricCard title="PEG Ratio" values={rat.valuation?.pegRatio} unit="x" good="low" accent={ACCENT} tooltip="P/E ÷ EPS Growth. PEG < 1 = undervalued growth"/>
                  </div>
                </Section>
                <Section title="EV Multiples" accent={ACCENT2}>
                  <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(185px,1fr))",gap:12 }}>
                    <MetricCard title="EV/EBITDA" values={rat.valuation?.evToEbitda} unit="x" good="low" accent={ACCENT2} tooltip="Most used M&A metric. <10x often reasonable"/>
                    <MetricCard title="EV/EBIT" values={rat.valuation?.evToEbit} unit="x" good="low" accent={ACCENT2} tooltip="Conservative — includes D&A"/>
                    <MetricCard title="EV/Sales" values={rat.valuation?.evToSales} unit="x" good="low" accent={ACCENT2} tooltip="Used for low-margin companies"/>
                    <MetricCard title="EV/FCF" values={rat.valuation?.evToFcf} unit="x" good="low" accent={ACCENT2} tooltip="Gold standard multiple. Lower = better"/>
                    <MetricCard title="FCF Yield" values={rat.valuation?.fcfYield} unit="%" good="high" accent={ACCENT} tooltip="FCF / Market Cap. >5% is attractive"/>
                  </div>
                </Section>
              </div>
            )}

            {/* ════ LEVERAGE ════ */}
            {tab==="leverage"&&(
              <div style={{ animation:"fadeUp 0.3s ease" }}>
                <Section title="Debt & Leverage" accent="#E74C3C" sub="Financial risk from debt obligations">
                  <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(185px,1fr))",gap:12 }}>
                    <MetricCard title="Debt/Equity" values={rat.leverage?.debtToEquity} unit="x" good="low" tooltip="<1x healthy. High D/E = higher risk"/>
                    <MetricCard title="Debt/EBITDA" values={rat.leverage?.debtToEbitda} unit="x" good="low" tooltip="Years to repay debt. <3x comfortable"/>
                    <MetricCard title="Net Debt/EBITDA" values={rat.leverage?.netDebtToEbitda} unit="x" good="low" tooltip="True leverage after cash. Negative = net cash"/>
                    <MetricCard title="Interest Coverage" values={rat.leverage?.interestCoverage} unit="x" good="high" tooltip="EBIT / Interest. <3x is a warning sign"/>
                    <MetricCard title="Debt/Assets" values={rat.leverage?.debtToAssets} unit="%" good="low" tooltip=">50% raises solvency concerns"/>
                  </div>
                </Section>
              </div>
            )}

            {/* ════ LIQUIDITY ════ */}
            {tab==="liquidity"&&(
              <div style={{ animation:"fadeUp 0.3s ease" }}>
                <Section title="Liquidity Ratios" accent="#3498DB" sub="Ability to meet short-term obligations">
                  <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(185px,1fr))",gap:12 }}>
                    <MetricCard title="Current Ratio" values={rat.liquidity?.currentRatio} unit="x" good="high" accent="#3498DB" tooltip=">1.5x preferred. <1x = stress"/>
                    <MetricCard title="Quick Ratio" values={rat.liquidity?.quickRatio} unit="x" good="high" accent="#3498DB" tooltip="Excludes inventory. >1x safe"/>
                    <MetricCard title="Cash Ratio" values={rat.liquidity?.cashRatio} unit="x" good="high" accent="#3498DB" tooltip="Most conservative measure"/>
                    <MetricCard title="OCF Ratio" values={rat.liquidity?.operatingCashFlowRatio} unit="x" good="high" accent={ACCENT} tooltip="Cash-based — harder to manipulate"/>
                  </div>
                </Section>
              </div>
            )}

            {/* ════ EFFICIENCY ════ */}
            {tab==="efficiency"&&(
              <div style={{ animation:"fadeUp 0.3s ease" }}>
                <Section title="Turnover Ratios" accent="#27AE60">
                  <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(185px,1fr))",gap:12 }}>
                    <MetricCard title="Asset Turnover" values={rat.efficiency?.assetTurnover} unit="x" good="high" tooltip="Revenue / Assets. Higher = leaner model"/>
                    <MetricCard title="Inventory Turnover" values={rat.efficiency?.inventoryTurnover} unit="x" good="high" tooltip="How fast inventory is sold"/>
                    <MetricCard title="Receivables Turnover" values={rat.efficiency?.receivablesTurnover} unit="x" good="high" tooltip="How quickly customers pay"/>
                  </div>
                </Section>
                <Section title="Working Capital Cycle" accent={ACCENT} sub="CCC = DSO + DIO – DPO. Negative is excellent">
                  <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(185px,1fr))",gap:12 }}>
                    <MetricCard title="DSO" values={rat.efficiency?.dso} unit=" d" good="low" tooltip="Days to collect from customers"/>
                    <MetricCard title="DIO" values={rat.efficiency?.dio} unit=" d" good="low" tooltip="Days inventory held"/>
                    <MetricCard title="DPO" values={rat.efficiency?.dpo} unit=" d" good="high" tooltip="Days to pay suppliers. Higher = better WC"/>
                    <MetricCard title="Cash Conv. Cycle" values={rat.efficiency?.cashConversionCycle} unit=" d" good="low" accent={ACCENT} tooltip="Full WC cycle. Negative CCC is excellent"/>
                  </div>
                </Section>
              </div>
            )}

            {/* ════ VALUE CREATION ════ */}
            {tab==="value"&&(
              <div style={{ animation:"fadeUp 0.3s ease" }}>
                <Section title="Economic Value Added" accent={ACCENT} sub="EVA = NOPAT – (WACC × Invested Capital). Positive = genuine wealth creation">
                  <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(185px,1fr))",gap:12 }}>
                    <MetricCard title="EVA" values={rat.valueCreation?.eva} unit={cur} good="high" accent={ACCENT} tooltip="Positive EVA = earns above cost of capital"/>
                    <MetricCard title="NOPAT" values={fin.nopat} unit={cur} good="high" tooltip="Net Operating Profit After Tax"/>
                    <MetricCard title="WACC %" values={fin.wacc} unit="%" good="low" tooltip="Hurdle rate ROIC must beat"/>
                    <MetricCard title="Economic Spread" values={rat.valueCreation?.economicSpread} unit="%" good="high" accent={ACCENT} tooltip="ROIC – WACC. Positive = value creation"/>
                  </div>
                </Section>
                <Section title="Quality & Distress Scores" accent="#9B59B6">
                  <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(185px,1fr))",gap:12 }}>
                    <MetricCard title="Altman Z-Score" values={rat.valueCreation?.altmanZScore} unit="" good="high" accent="#9B59B6" tooltip="Z>2.99=Safe · 1.81-2.99=Grey · <1.81=Distress"/>
                    <MetricCard title="Piotroski F-Score" values={rat.valueCreation?.piotroskiScore} unit="/9" good="high" accent={ACCENT2} tooltip="7-9=Strong · 3-6=Average · 0-2=Weak"/>
                    <MetricCard title="FCF Conversion" values={rat.valueCreation?.fcfConversion} unit="%" good="high" accent="#27AE60" tooltip="FCF/Net Profit. >80% = earnings backed by real cash"/>
                  </div>
                </Section>
              </div>
            )}

            {/* ════ CASH FLOW ════ */}
            {tab==="cashflow"&&(
              <div style={{ animation:"fadeUp 0.3s ease" }}>
                <Section title="Cash Flow" accent={ACCENT2}>
                  <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(185px,1fr))",gap:12,marginBottom:16 }}>
                    <MetricCard title="Operating Cash Flow" values={fin.operatingCashFlow} unit={cur} good="high" tooltip="Core business cash generation"/>
                    <MetricCard title="Free Cash Flow" values={fin.freeCashFlow} unit={cur} good="high" accent={ACCENT} tooltip="OCF – CapEx. Truest measure of health"/>
                    <MetricCard title="CapEx" values={fin.capex} unit={cur} good="low" tooltip="Capital investment in fixed assets"/>
                    <MetricCard title="Dividends Paid" values={fin.dividendsPaid} unit={cur} good="low" tooltip="Cash returned to shareholders"/>
                  </div>
                </Section>
                <Section title="Growth Rates (YoY %)" accent="#27AE60">
                  <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(185px,1fr))",gap:12 }}>
                    <MetricCard title="Revenue Growth" values={rat.growth?.revenueGrowth} unit="%" good="high" tooltip="YoY revenue growth"/>
                    <MetricCard title="EBITDA Growth" values={rat.growth?.ebitdaGrowth} unit="%" good="high" tooltip="Should exceed revenue growth"/>
                    <MetricCard title="Net Profit Growth" values={rat.growth?.netProfitGrowth} unit="%" good="high" tooltip="Consistent double-digit = quality"/>
                    <MetricCard title="EPS Growth" values={rat.growth?.epsGrowth} unit="%" good="high" tooltip="PEG <1 when EPS growth > P/E"/>
                    <MetricCard title="FCF Growth" values={rat.growth?.fcfGrowth} unit="%" good="high" accent={ACCENT} tooltip="Most reliable growth metric"/>
                  </div>
                </Section>
              </div>
            )}

            {/* ════ DCF + GORDON INTRINSIC VALUE ════ */}
            {tab==="dcf"&&(
              <div style={{ animation:"fadeUp 0.3s ease" }}>

                {/* Analyst assumption note */}
                {R.dcfAssumptions?.analystNote&&(
                  <div style={{ background:"#0D1B2E",border:`1px solid ${ACCENT}33`,borderRadius:12,padding:"14px 18px",marginBottom:24,fontSize:13,color:"#8DB4A0",lineHeight:1.7 }}>
                    <span style={{ color:ACCENT,fontWeight:700 }}>🤖 AI Assumption Rationale: </span>
                    {R.dcfAssumptions.analystNote}
                  </div>
                )}

                <div style={{ display:"grid",gridTemplateColumns:"340px 1fr",gap:20,marginBottom:28 }}>

                  {/* ─ Assumption Sliders ─ */}
                  <div style={{ background:CARD,border:`1px solid ${BORDER}`,borderRadius:14,padding:"20px 22px" }}>
                    <div style={{ fontSize:12,fontWeight:700,color:"#CBD5E1",textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:18,display:"flex",alignItems:"center",gap:8 }}>
                      <span style={{ fontSize:16 }}>⚙️</span> Model Assumptions
                    </div>
                    <div style={{ fontSize:11,color:"#8BA3BC",marginBottom:16 }}>Adjust sliders — valuation updates live</div>

                    <div style={{ fontSize:11,fontWeight:700,color:"#8899AA",textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:10,marginTop:4 }}>DCF Inputs</div>
                    <SliderInput label="Phase 1 FCF Growth (Yrs 1–5)" value={dcfA.growthRate1} onChange={v=>setDcfA(a=>({...a,growthRate1:v}))} min={2} max={35} step={0.5} unit="%" tooltip="Expected FCF growth rate for first 5 years. Use recent historical FCF CAGR as anchor"/>
                    <SliderInput label="Phase 2 FCF Growth (Yrs 6–10)" value={dcfA.growthRate2} onChange={v=>setDcfA(a=>({...a,growthRate2:v}))} min={2} max={25} step={0.5} unit="%" tooltip="Growth rate for years 6–10 as the business matures. Should be lower than Phase 1"/>
                    <SliderInput label="Terminal Growth Rate" value={dcfA.terminalGrowth} onChange={v=>setDcfA(a=>({...a,terminalGrowth:v}))} min={1} max={7} step={0.5} unit="%" tooltip="Perpetual growth rate after Year 10. Should not exceed long-run GDP growth (~5-6% for India, ~2-3% for US)"/>
                    <SliderInput label="WACC (Discount Rate)" value={dcfA.wacc} onChange={v=>setDcfA(a=>({...a,wacc:v}))} min={6} max={20} step={0.5} unit="%" tooltip="Weighted Average Cost of Capital. Higher WACC = lower valuation. Typically 9–14% for Indian large-caps"/>

                    <div style={{ fontSize:11,fontWeight:700,color:"#8899AA",textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:10,marginTop:18 }}>Gordon Growth Model Inputs</div>
                    <SliderInput label="Dividend Per Share" value={dcfA.dividendPerShare} onChange={v=>setDcfA(a=>({...a,dividendPerShare:v}))} min={0} max={500} step={1} unit={sym} tooltip="Latest annual dividend per share. Gordon model works best for dividend-paying companies"/>
                    <SliderInput label="Expected Dividend Growth" value={dcfA.dividendGrowth} onChange={v=>setDcfA(a=>({...a,dividendGrowth:v}))} min={1} max={15} step={0.5} unit="%" tooltip="Expected long-run dividend growth rate. Should approximate earnings growth"/>
                    <SliderInput label="Cost of Equity (Ke)" value={dcfA.costOfEquity} onChange={v=>setDcfA(a=>({...a,costOfEquity:v}))} min={6} max={20} step={0.5} unit="%" tooltip="Required return on equity = Risk-Free Rate + Beta × Equity Risk Premium. Typically 11–15% for Indian equities"/>
                  </div>

                  {/* ─ Results Panel ─ */}
                  <div style={{ display:"flex",flexDirection:"column",gap:14 }}>

                    {/* Method Results */}
                    <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:14 }}>

                      {/* DCF Result */}
                      <div style={{ background:"linear-gradient(135deg,#0D1B2E,#111827)",border:`1px solid ${ACCENT}44`,borderRadius:14,padding:"20px 22px" }}>
                        <div style={{ fontSize:11,color:ACCENT,textTransform:"uppercase",letterSpacing:"0.1em",fontWeight:700,marginBottom:16 }}>📐 DCF Valuation</div>
                        {dcfResult ? (<>
                          <div style={{ marginBottom:12 }}>
                            <div style={{ fontSize:11,color:"#8BA3BC" }}>PV of FCFs (Phase 1)</div>
                            <div style={{ fontSize:15,color:"#CBD5E1",fontFamily:"'Space Mono',monospace" }}>{sym}{Math.round(dcfResult.pvPhase1).toLocaleString()} {cur.split(" ")[1]||""}</div>
                          </div>
                          <div style={{ marginBottom:12 }}>
                            <div style={{ fontSize:11,color:"#8BA3BC" }}>PV of FCFs (Phase 2)</div>
                            <div style={{ fontSize:15,color:"#CBD5E1",fontFamily:"'Space Mono',monospace" }}>{sym}{Math.round(dcfResult.pvPhase2).toLocaleString()} {cur.split(" ")[1]||""}</div>
                          </div>
                          <div style={{ marginBottom:12 }}>
                            <div style={{ fontSize:11,color:"#8BA3BC" }}>PV of Terminal Value</div>
                            <div style={{ fontSize:15,color:"#FFB300",fontFamily:"'Space Mono',monospace" }}>{sym}{dcfResult.pvTerminal.toLocaleString()} ({dcfResult.tvWeightPct}% of EV)</div>
                          </div>
                          <div style={{ borderTop:`1px solid ${BORDER}`,paddingTop:12,marginTop:4 }}>
                            <div style={{ fontSize:11,color:"#8BA3BC" }}>Enterprise Value</div>
                            <div style={{ fontSize:15,color:"#CBD5E1",fontFamily:"'Space Mono',monospace" }}>{sym}{dcfResult.enterpriseValue.toLocaleString()}</div>
                          </div>
                          <div style={{ marginTop:8 }}>
                            <div style={{ fontSize:11,color:"#8BA3BC" }}>Less: Net Debt</div>
                            <div style={{ fontSize:14,color:"#CBD5E1",fontFamily:"'Space Mono',monospace" }}>({sym}{(dcfA.netDebt||0).toLocaleString()})</div>
                          </div>
                          <div style={{ background:"#0A0F1E",borderRadius:10,padding:"14px",marginTop:14,textAlign:"center" }}>
                            <div style={{ fontSize:11,color:"#8BA3BC",marginBottom:4 }}>DCF Intrinsic Value Per Share</div>
                            <div style={{ fontSize:28,fontWeight:700,color:ACCENT,fontFamily:"'Space Mono',monospace" }}>{sym}{dcfResult.intrinsicPerShare.toLocaleString()}</div>
                          </div>
                        </>) : <div style={{ color:"#8BA3BC",fontSize:12 }}>Set valid assumptions above</div>}
                      </div>

                      {/* Gordon Result */}
                      <div style={{ background:"linear-gradient(135deg,#0D1B2E,#111827)",border:`1px solid #FFB30044`,borderRadius:14,padding:"20px 22px" }}>
                        <div style={{ fontSize:11,color:"#FFB300",textTransform:"uppercase",letterSpacing:"0.1em",fontWeight:700,marginBottom:16 }}>📈 Gordon Growth Model</div>
                        <div style={{ fontSize:12,color:"#8BA3BC",lineHeight:1.7,marginBottom:16 }}>
                          Formula: <span style={{ color:"#CBD5E1",fontFamily:"'Space Mono',monospace" }}>P = D₁ ÷ (Ke – g)</span>
                          <br/>Where D₁ = next year's dividend, Ke = cost of equity, g = dividend growth rate
                        </div>
                        {gordResult ? (<>
                          <div style={{ marginBottom:10 }}>
                            <div style={{ fontSize:11,color:"#8BA3BC" }}>D₀ (Current DPS)</div>
                            <div style={{ fontSize:15,color:"#CBD5E1",fontFamily:"'Space Mono',monospace" }}>{sym}{dcfA.dividendPerShare}</div>
                          </div>
                          <div style={{ marginBottom:10 }}>
                            <div style={{ fontSize:11,color:"#8BA3BC" }}>D₁ (Next year DPS)</div>
                            <div style={{ fontSize:15,color:"#CBD5E1",fontFamily:"'Space Mono',monospace" }}>{sym}{gordResult.d1}</div>
                          </div>
                          <div style={{ marginBottom:10 }}>
                            <div style={{ fontSize:11,color:"#8BA3BC" }}>Ke – g (Spread)</div>
                            <div style={{ fontSize:15,color:"#CBD5E1",fontFamily:"'Space Mono',monospace" }}>{(dcfA.costOfEquity - dcfA.dividendGrowth).toFixed(1)}%</div>
                          </div>
                          <div style={{ background:"#0A0F1E",borderRadius:10,padding:"14px",marginTop:14,textAlign:"center" }}>
                            <div style={{ fontSize:11,color:"#8BA3BC",marginBottom:4 }}>Gordon Growth Intrinsic Value</div>
                            <div style={{ fontSize:28,fontWeight:700,color:"#FFB300",fontFamily:"'Space Mono',monospace" }}>{sym}{gordResult.intrinsicPerShare.toLocaleString()}</div>
                          </div>
                        </>) : (
                          <div style={{ color:"#8BA3BC",fontSize:12 }}>
                            {dcfA.costOfEquity<=dcfA.dividendGrowth
                              ? "⚠ Cost of equity must exceed dividend growth rate"
                              : "Set valid dividend inputs above"}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Blended Verdict */}
                    {blendedIV&&(
                      <div style={{ background:"linear-gradient(135deg,#0A1628,#0D1B2E)",border:`2px solid ${ivRecColor}55`,borderRadius:14,padding:"22px 26px",animation:"glow 3s infinite" }}>
                        <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:16 }}>
                          <div>
                            <div style={{ fontSize:11,color:"#8BA3BC",textTransform:"uppercase",letterSpacing:"0.1em",marginBottom:6 }}>Blended Intrinsic Value (Average of Both Methods)</div>
                            <div style={{ fontSize:38,fontWeight:700,color:"#FFB300",fontFamily:"'Space Mono',monospace" }}>{sym}{blendedIV.toLocaleString()}</div>
                          </div>
                          <div style={{ textAlign:"right" }}>
                            <div style={{ fontSize:11,color:"#8BA3BC",marginBottom:4 }}>Current Market Price</div>
                            <div style={{ fontSize:22,color:"#CBD5E1",fontFamily:"'Space Mono',monospace",marginBottom:8 }}>{R.company?.currentPrice}</div>
                            <div style={{ fontSize:14,color:parseFloat(marginOfSafety)>0?"#00E5BE":"#FF4D4D",fontWeight:700 }}>
                              {parseFloat(marginOfSafety)>0
                                ? `${marginOfSafety}% Margin of Safety`
                                : `${Math.abs(marginOfSafety)}% Premium to Fair Value`}
                            </div>
                          </div>
                          <div style={{ textAlign:"center" }}>
                            <div style={{ fontSize:11,color:"#8BA3BC",marginBottom:8 }}>Valuation-Based Signal</div>
                            <div style={{ background:ivRecColor+"22",border:`2px solid ${ivRecColor}66`,color:ivRecColor,padding:"12px 28px",borderRadius:28,fontSize:18,fontWeight:700,letterSpacing:"0.12em" }}>{ivRec}</div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* FCF Projection Waterfall */}
                {dcfResult&&(
                  <Section title="Projected Free Cash Flow Waterfall (10 Years)" accent={ACCENT} sub="Phase 1 (high growth) in teal · Phase 2 (fade) in orange · Terminal value shown separately">
                    <div style={{ background:CARD,border:`1px solid ${BORDER}`,borderRadius:14,padding:"20px 22px" }}>
                      <div style={{ overflowX:"auto" }}>
                        <table style={{ width:"100%",borderCollapse:"collapse",fontSize:12,minWidth:600 }}>
                          <thead>
                            <tr style={{ borderBottom:`1px solid ${BORDER}` }}>
                              {["Year","FCF (Projected)","PV Factor","PV of FCF","Phase"].map(h=>(
                                <th key={h} style={{ padding:"8px 12px",textAlign:"right",color:"#94A3B8",fontSize:10,textTransform:"uppercase",letterSpacing:"0.07em",fontWeight:600,whiteSpace:"nowrap" }}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {dcfResult.pvFCFs.map((row,i)=>(
                              <tr key={i} style={{ borderBottom:`1px solid #0D1B2E` }}>
                                <td style={{ padding:"9px 12px",textAlign:"right",fontFamily:"'Space Mono',monospace",color:"#CBD5E1" }}>{row.year}</td>
                                <td style={{ padding:"9px 12px",textAlign:"right",fontFamily:"'Space Mono',monospace",color:"#CBD5E1" }}>{sym}{row.fcf.toLocaleString()}</td>
                                <td style={{ padding:"9px 12px",textAlign:"right",fontFamily:"'Space Mono',monospace",color:"#94A3B8" }}>{(1/Math.pow(1+dcfA.wacc/100,i+1)).toFixed(4)}</td>
                                <td style={{ padding:"9px 12px",textAlign:"right",fontFamily:"'Space Mono',monospace",color:row.phase===1?ACCENT:ACCENT2 }}>{sym}{row.pv.toLocaleString()}</td>
                                <td style={{ padding:"9px 12px",textAlign:"right" }}>
                                  <span style={{ background:row.phase===1?ACCENT+"22":ACCENT2+"22",color:row.phase===1?ACCENT:ACCENT2,padding:"2px 8px",borderRadius:10,fontSize:10 }}>Ph.{row.phase}</span>
                                </td>
                              </tr>
                            ))}
                            <tr style={{ borderTop:`2px solid ${BORDER}` }}>
                              <td colSpan={2} style={{ padding:"10px 12px",color:"#FFB300",fontWeight:700 }}>Terminal Value (Gordon Growth)</td>
                              <td style={{ padding:"10px 12px",textAlign:"right",color:"#8BA3BC",fontFamily:"'Space Mono',monospace",fontSize:11 }}>
                                {(1/Math.pow(1+dcfA.wacc/100,10)).toFixed(4)}
                              </td>
                              <td style={{ padding:"10px 12px",textAlign:"right",color:"#FFB300",fontFamily:"'Space Mono',monospace",fontWeight:700 }}>{sym}{dcfResult.pvTerminal.toLocaleString()}</td>
                              <td style={{ padding:"10px 12px",textAlign:"right",color:"#FFB300",fontSize:11 }}>{dcfResult.tvWeightPct}% of EV</td>
                            </tr>
                            <tr style={{ background:"#0D1B2E" }}>
                              <td colSpan={2} style={{ padding:"10px 12px",color:ACCENT,fontWeight:700 }}>Enterprise Value</td>
                              <td/>
                              <td style={{ padding:"10px 12px",textAlign:"right",color:ACCENT,fontFamily:"'Space Mono',monospace",fontWeight:700 }}>{sym}{dcfResult.enterpriseValue.toLocaleString()}</td>
                              <td/>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </Section>
                )}

                {/* Sensitivity Matrix */}
                {sens&&(
                  <Section title="Sensitivity Analysis — Intrinsic Value Per Share" accent="#9B59B6" sub={`Rows = WACC · Columns = Terminal Growth Rate. Highlighted cell = current assumptions`}>
                    <div style={{ background:CARD,border:`1px solid ${BORDER}`,borderRadius:14,padding:"20px 22px",overflowX:"auto" }}>
                      <table style={{ borderCollapse:"collapse",fontSize:12,minWidth:500 }}>
                        <thead>
                          <tr>
                            <th style={{ padding:"8px 14px",color:"#8BA3BC",fontSize:11,textAlign:"center",background:"#0D1B2E",borderRadius:"8px 0 0 0" }}>WACC ↓ / g →</th>
                            {sens.gtRange.map(g=>(
                              <th key={g} style={{ padding:"8px 14px",textAlign:"center",color:g===dcfA.terminalGrowth?"#FFB300":"#8899AA",fontSize:11,fontFamily:"'Space Mono',monospace",background:"#0D1B2E",fontWeight:g===dcfA.terminalGrowth?700:400 }}>{g}%</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {sens.waccRange.map((w,wi)=>(
                            <tr key={w}>
                              <td style={{ padding:"9px 14px",textAlign:"center",color:w===dcfA.wacc?"#FFB300":"#8899AA",fontFamily:"'Space Mono',monospace",fontSize:11,fontWeight:w===dcfA.wacc?700:400,background:"#0D1B2E" }}>{w}%</td>
                              {sens.gtRange.map((g,gi)=>{
                                const val = sens.cells[wi][gi];
                                const isCurrent = w===dcfA.wacc && g===dcfA.terminalGrowth;
                                const ratio = currentPriceNum&&val ? val/currentPriceNum : null;
                                const cellColor = !ratio ? "#8BA3BC" : ratio>1.3?"#00E5BE":ratio>1?"#8DB4A0":ratio>0.85?"#FFB300":"#FF6B6B";
                                return (
                                  <td key={g} style={{ padding:"9px 14px",textAlign:"center",fontFamily:"'Space Mono',monospace",fontSize:12,color:cellColor,background:isCurrent?"#1A2D1A":wi%2===0?"#0A0F1E":"#0D1B2E",border:isCurrent?`2px solid ${ACCENT}44`:"none",fontWeight:isCurrent?700:400,borderRadius:isCurrent?6:0 }}>
                                    {val!=null?`${sym}${val.toLocaleString()}`:"—"}
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <div style={{ display:"flex",gap:16,marginTop:14,flexWrap:"wrap" }}>
                        {[["#00E5BE",">30% upside"],["#8DB4A0","0–30% upside"],["#FFB300","0–15% downside"],["#FF6B6B",">15% downside"]].map(([c,l])=>(
                          <div key={l} style={{ display:"flex",alignItems:"center",gap:6 }}>
                            <div style={{ width:10,height:10,borderRadius:2,background:c }}/>
                            <span style={{ fontSize:11,color:"#94A3B8" }}>{l}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </Section>
                )}

                {/* Methodology Note */}
                <div style={{ background:"#0A0F1E",border:`1px solid ${BORDER}`,borderRadius:12,padding:"18px 22px",fontSize:11,color:"#94A3B8",lineHeight:1.8 }}>
                  <div style={{ fontWeight:700,color:"#8899AA",marginBottom:8 }}>📖 Methodology Notes</div>
                  <div><strong style={{ color:"#A8C4D8" }}>DCF (Discounted Cash Flow):</strong> Projects free cash flow over 10 years in two phases, then applies Gordon Growth Model for terminal value. All future cash flows are discounted at WACC to arrive at Enterprise Value. Net Debt is deducted to get Equity Value, divided by shares outstanding for intrinsic value per share.</div>
                  <div style={{ marginTop:6 }}><strong style={{ color:"#A8C4D8" }}>Gordon Growth Model (GGM):</strong> A dividend discount model. Intrinsic Value = D₁ ÷ (Ke – g). Best applied to mature, dividend-paying companies. Sensitive to the spread between cost of equity and growth rate.</div>
                  <div style={{ marginTop:6 }}><strong style={{ color:"#A8C4D8" }}>Blended Value:</strong> Simple average of DCF and GGM. The margin of safety is the discount between market price and blended intrinsic value.</div>
                  <div style={{ marginTop:10,padding:"10px 14px",background:"#111827",borderRadius:8,color:"#96B0C8" }}>⚠ Valuations are inherently sensitive to assumptions. This model is for educational and analytical purposes only — not investment advice. Always verify with official filings and consult a qualified financial advisor.</div>
                </div>

              </div>
            )}

          </div>
        )}
      </div>
    </div>
  );
}
